/** AffiliationBadges — compact chip row displaying a user's section
 *  affiliations. Colored by taxonomy:
 *    · junior_artist → amber
 *    · featured_cast → emerald
 *    · everything else → white/60
 *
 *  Consumers: Dashboard, DigitalICard, public profile page.
 *  Empty state renders nothing (silent no-op).
 */
import { MapPin, ShieldCheck, Handshake } from "lucide-react";

const TAXONOMY_COLOR = {
  junior_artist: "border-amber-500/40 text-amber-300 bg-amber-500/[0.06]",
  featured_cast: "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.06]",
};

export default function AffiliationBadges({
  items = [],
  className = "",
  compact = false,
  testidPrefix = "aff-badges",
}) {
  if (!items?.length) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid={testidPrefix}>
      {items.map((it) => {
        const tone = TAXONOMY_COLOR[it.taxonomy] || "border-white/15 text-white/70 bg-white/[0.03]";
        const size = compact ? "text-[10px] px-2 py-1" : "text-[11px] px-2.5 py-1.5";
        return (
          <div
            key={it.code}
            data-testid={`${testidPrefix}-${it.code}`}
            className={`inline-flex items-center gap-1.5 rounded-full border ${tone} ${size} tracking-[0.08em]`}
            title={it.full_name}
          >
            {it.verified
              ? <ShieldCheck size={compact ? 10 : 12} />
              : <Handshake  size={compact ? 10 : 12} className="opacity-70" />
            }
            <span className="font-semibold">{it.name}</span>
            {it.city && (
              <span className="inline-flex items-center gap-0.5 text-white/45 pl-1 border-l border-white/10">
                <MapPin size={compact ? 8 : 10} /> {it.city}
              </span>
            )}
            {!compact && it.membership_number && (
              <span className="font-mono opacity-70 pl-1 border-l border-white/10 text-[9px]">
                #{it.membership_number}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
